"use client";

import React from "react";
import { SlidersHorizontal, ShieldCheck, ArrowRight, FileText, CircleCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import Reveal from "@/components/common/Reveal";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
import { COMMON } from "@/lib/commonCopy";
interface HeroCopy {
  categoryBadge: string;
  standardBadge: string;
  title: string;
  lead: string;
  secondaryCta: string;
  imageAlt: string;
  imageChip: string;
  cardNote: string;
  cardLink: string;
  stripApertureLabel: string;
  stripApertureValue: string;
  stripMeshLabel: string;
  stripMeshValue: string;
  stripWireLabel: string;
  stripWireValue: string;
  stripStandardLabel: string;
  stripStandardValue: string;
}
const copyData: Record<Lang, HeroCopy> = {
  en: {
    categoryBadge: "Technical Engineering Resource",
    standardBadge: "ISO 9044 / ASTM E2016",
    title: "Wire Mesh & Industrial Filtration Fundamentals",
    lead: "An engineering reference guide to wire diameter selection, aperture geometry calculations, weave pattern fluid dynamics, and stainless steel alloy selection for high-performance industrial separation processes.",
    secondaryCta: "Calculation Guides",
    imageAlt: "Stainless steel woven wire mesh industrial inspection and weave geometry",
    imageChip: "Woven & Sintered Media",
    cardNote: "Technical specifications & CAD drawings",
    cardLink: "Downloads",
    stripApertureLabel: "Aperture Range",
    stripApertureValue: "1 µm – 25 mm",
    stripMeshLabel: "Mesh Count Range",
    stripMeshValue: "1 – 635 Mesh",
    stripWireLabel: "Wire Diameter",
    stripWireValue: "0.020 – 2.0 mm",
    stripStandardLabel: "Manufacturing Standard",
    stripStandardValue: "ISO 9044 / ASTM E2016"
  },
  zh: {
    categoryBadge: "工程技术资源",
    standardBadge: "ISO 9044 / ASTM E2016",
    title: "工业丝网与过滤工程基础指南",
    lead: "面向工业分离与过滤工程的技术参考指南，系统解析丝径选型、孔径几何计算、编织结构流体力学特征以及不锈钢合金选材准则。",
    secondaryCta: "计算指南",
    imageAlt: "不锈钢编织丝网结构检测与几何细节",
    imageChip: "编织与烧结滤材",
    cardNote: "技术规格书与 CAD 图纸",
    cardLink: "下载中心",
    stripApertureLabel: "孔径范围",
    stripApertureValue: "1 µm – 25 mm",
    stripMeshLabel: "目数范围",
    stripMeshValue: "1 – 635 目",
    stripWireLabel: "丝径范围",
    stripWireValue: "0.020 – 2.0 mm",
    stripStandardLabel: "制造执行标准",
    stripStandardValue: "ISO 9044 / ASTM E2016"
  },
  es: {
    categoryBadge: "Recurso de ingeniería técnica",
    standardBadge: "ISO 9044 / ASTM E2016",
    title: "Fundamentos de malla metálica y filtración industrial",
    lead: "Guía de referencia técnica para selección de diámetro de alambre, cálculos geométricos de apertura, dinámica de fluidos según el tejido y selección de aleaciones de acero inoxidable para separación industrial.",
    secondaryCta: "Guías de cálculo",
    imageAlt: "Inspección técnica de malla de alambre tejido de acero inoxidable",
    imageChip: "Medios tejidos y sinterizados",
    cardNote: "Especificaciones técnicas y planos CAD",
    cardLink: "Descargas",
    stripApertureLabel: "Rango de apertura",
    stripApertureValue: "1 µm – 25 mm",
    stripMeshLabel: "Rango de mallas",
    stripMeshValue: "1 – 635 Mesh",
    stripWireLabel: "Diámetro de alambre",
    stripWireValue: "0.020 – 2.0 mm",
    stripStandardLabel: "Norma de fabricación",
    stripStandardValue: "ISO 9044 / ASTM E2016"
  },
  de: {
    categoryBadge: "Technische Engineering-Ressource",
    standardBadge: "ISO 9044 / ASTM E2016",
    title: "Grundlagen zu Drahtgewebe & industrieller Filtration",
    lead: "Ein technischer Leitfaden zur Drahtdurchmesserauswahl, Maschenweitenberechnung, Strömungsdynamik von Webarten und Edelstahl-Werkstoffauswahl für industrielle Trennprozesse.",
    secondaryCta: "Berechnungsleitfäden",
    imageAlt: "Industrielle Qualitätsprüfung von Edelstahl-Drahtgewebe",
    imageChip: "Gewebe- & Sintermedien",
    cardNote: "Technische Datenblätter & CAD-Zeichnungen",
    cardLink: "Downloads",
    stripApertureLabel: "Maschenweitenbereich",
    stripApertureValue: "1 µm – 25 mm",
    stripMeshLabel: "Maschenzahlbereich",
    stripMeshValue: "1 – 635 Mesh",
    stripWireLabel: "Drahtdurchmesser",
    stripWireValue: "0.020 – 2.0 mm",
    stripStandardLabel: "Fertigungsnorm",
    stripStandardValue: "ISO 9044 / ASTM E2016"
  },
  fr: {
    categoryBadge: "Ressource d'ingénierie technique",
    standardBadge: "ISO 9044 / ASTM E2016",
    title: "Fondamentaux de la toile métallique et de la filtration industrielle",
    lead: "Guide de référence technique pour le choix du diamètre de fil, le calcul géométrique des ouvertures, la dynamique des fluides selon le tissage et la sélection des nuances d'acier inoxydable pour la séparation industrielle.",
    secondaryCta: "Guides de calcul",
    imageAlt: "Contrôle technique de toile métallique tissée en acier inoxydable",
    imageChip: "Médias tissés et frittés",
    cardNote: "Spécifications techniques et plans CAD",
    cardLink: "Téléchargements",
    stripApertureLabel: "Plage d'ouverture",
    stripApertureValue: "1 µm – 25 mm",
    stripMeshLabel: "Gamme de mailles",
    stripMeshValue: "1 – 635 Mesh",
    stripWireLabel: "Diamètre de fil",
    stripWireValue: "0.020 – 2.0 mm",
    stripStandardLabel: "Norme de fabrication",
    stripStandardValue: "ISO 9044 / ASTM E2016"
  },
  pt: {
    categoryBadge: "Recurso de engenharia técnica",
    standardBadge: "ISO 9044 / ASTM E2016",
    title: "Fundamentos de malha metálica e filtração industrial",
    lead: "Guia de referência técnica para seleção de diâmetro de arame, cálculo geométrico de abertura, dinâmica de fluidos conforme a trama e especificação de ligas de aço inoxidável para separação industrial.",
    secondaryCta: "Guias de cálculo",
    imageAlt: "Inspeção técnica e geometria de malha tecida de aço inoxidável",
    imageChip: "Meios tecidos e sinterizados",
    cardNote: "Especificações técnicas e desenhos CAD",
    cardLink: "Downloads",
    stripApertureLabel: "Faixa de abertura",
    stripApertureValue: "1 µm – 25 mm",
    stripMeshLabel: "Faixa de malha",
    stripMeshValue: "1 – 635 Mesh",
    stripWireLabel: "Diâmetro do arame",
    stripWireValue: "0.020 – 2.0 mm",
    stripStandardLabel: "Norma de fabricação",
    stripStandardValue: "ISO 9044 / ASTM E2016"
  }
};
export default function HeroFundamentals() {
  const {
    lang
  } = useLanguage();
  const t = pick(lang, copyData);
  const specHighlights = [{
    label: t.stripApertureLabel,
    value: t.stripApertureValue
  }, {
    label: t.stripMeshLabel,
    value: t.stripMeshValue
  }, {
    label: t.stripWireLabel,
    value: t.stripWireValue
  }, {
    label: t.stripStandardLabel,
    value: t.stripStandardValue
  }];
  return <section id="herofundamentals" data-nav="light" className="relative overflow-hidden bg-background mf-hero border-b border-border text-foreground">
      <div className="mf-container relative">
        <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12">
          {/* Left Column (7 columns on lg) */}
          <Reveal className="lg:col-span-7 flex flex-col items-start">
            {/* Exactly two badges */}
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="gap-1.5 rounded-md border-border bg-card px-3 py-1 font-mono mf-caption tracking-tight text-foreground">
                <SlidersHorizontal className="size-3.5 text-primary" aria-hidden="true" />
                <span>{t.categoryBadge}</span>
              </Badge>
              <Badge variant="outline" className="gap-1.5 rounded-md border-border bg-muted px-3 py-1 font-mono mf-caption text-foreground">
                <ShieldCheck className="size-3.5 text-primary" aria-hidden="true" />
                <span>{t.standardBadge}</span>
              </Badge>
            </div>

            {/* Main Headline */}
            <h1 className="font-default text-balance mf-h1 text-foreground">
              {t.title}
            </h1>

            {/* Lead Paragraph */}
            <p className="mt-4 max-w-2xl text-muted-foreground mf-lead">
              {t.lead}
            </p>

            {/* CTAs */}
            <div className="mt-6 flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
              <Button size="lg" asChild className="font-semibold transition-colors">
                <Link to="/RequestAQuotePage" className="inline-flex items-center gap-2">
                  <span>{pick(lang, COMMON.requestQuote)}</span>
                  <ArrowRight className="size-4" aria-hidden="true" />
                </Link>
              </Button>

              <Button variant="outline" size="lg" asChild className="font-semibold transition-colors">
                <Link to="/CalculationGuidesPage" className="inline-flex items-center gap-2">
                  <FileText className="size-4" aria-hidden="true" />
                  <span>{t.secondaryCta}</span>
                </Link>
              </Button>
            </div>
          </Reveal>

          {/* Right Column (5 columns on lg) */}
          <Reveal delay={50} className="lg:col-span-5 w-full">
            <Card className="overflow-hidden rounded-lg border border-border bg-card gap-0 py-0">
              {/* Product Visual Container */}
              <div className="mf-card-media mf-ratio-hero relative w-full overflow-hidden">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-photograph-of-woven-stainles-stee-8268b8.png" alt={t.imageAlt} className="h-full w-full object-cover object-center" loading="eager" fetchPriority="high" decoding="async" />
                <div className="absolute top-3 left-3 bg-card border border-border px-3 py-1 rounded-md mf-caption font-mono flex items-center gap-1.5 text-foreground">
                  <SlidersHorizontal className="size-3.5 text-primary" aria-hidden="true" />
                  <span>{t.imageChip}</span>
                </div>
              </div>

              {/* Single Footer Row */}
              <CardContent className="p-4">
                <div className="flex items-center justify-between mf-caption text-muted-foreground">
                  <div className="flex items-center gap-1.5 truncate">
                    <CircleCheck className="size-3.5 text-primary shrink-0" aria-hidden="true" />
                    <span className="truncate">{t.cardNote}</span>
                  </div>
                  <Link to="/DownloadsPage" className="font-medium text-primary hover:underline whitespace-nowrap ml-2 flex items-center gap-1 shrink-0 mf-caption">
                    <span>{t.cardLink}</span>
                    <ArrowRight className="size-3.5" aria-hidden="true" />
                  </Link>
                </div>
              </CardContent>
            </Card>
          </Reveal>
        </div>

        {/* Spec Strip below grid */}
        <div className="mf-spec-strip">
          {specHighlights.map((spec, idx) => <div key={idx} className="mf-spec-strip__item">
              <span className="mf-spec-strip__label">{spec.label}</span>
              <span className="mf-spec-strip__value">{spec.value}</span>
            </div>)}
        </div>
      </div>
    </section>;
}